import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_GATEWAYS_RESOURCE } from 'src/features/gateway/api/useListGateways';
import { MEMBERSHIP_CONFIGURATION_RESOURCE } from 'src/features/membership/api/useGetMembershipConfiguration';
import { useCreateMembershipZoneRule } from 'src/features/membership/api/zoneRule/useCreateMembershipZoneRule';
import { LIST_MEMBERSHIP_ZONE_RULES_RESOURCE } from 'src/features/membership/api/zoneRule/useListMembershipZoneRules';
import MembershipZoneRuleFormModal from 'src/features/membership/components/forms/zoneRule/MembershipZoneRuleFormModal';
import { zoneRulesMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface CreateZoneRuleFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
}

function CreateMembershipZoneRuleFormModal<TData>({ className, children }: CreateZoneRuleFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { membershipId = '' } = useParams();

    const { parkingId } = useParking();
    const { mutate, isPending } = useCreateMembershipZoneRule({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_ZONE_RULES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAYS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_CONFIGURATION_RESOURCE] });

            toast({
                title: formatMessage(zoneRulesMessages.createZoneRuleSuccess),
                description: formatMessage(zoneRulesMessages.createZoneRuleSuccessDescription),
            });
        },
    });

    return (
        <MembershipZoneRuleFormModal
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, parkingId, membershipId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.createEntry, {
                entry: formatMessage(zoneRulesMessages.zoneRule).toLowerCase(),
            })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(zoneRulesMessages.addZoneRuleModalSubtitle)}
            children={children}
        />
    );
}

export default CreateMembershipZoneRuleFormModal;
